import { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { Icons } from "../lib/icons";
import { Btn } from "../lib/primitives";
import { useTweaks } from "../lib/tweaks";
import { nextThemePref, THEME_PREF_LABEL } from "../lib/theme";
import { getSession, signOut } from "../lib/auth";
import { useProviderSwitcher } from "../lib/useProviderSwitcher";

function initialsOf(name: string, email: string): string {
  const source = name?.trim() || email || "";
  const parts = source.split(/[\s@.]+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "?";
  const second = parts[1]?.[0] ?? "";
  return (first + second).toUpperCase();
}

export function Topbar() {
  const { tweaks, setTweak } = useTweaks();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ["session"],
    queryFn: getSession,
    retry: false,
    staleTime: 60_000,
  });

  // Provider switcher state, shared with the sidebar.
  const switcher = useProviderSwitcher();

  const [menuOpen, setMenuOpen] = useState(false);
  const [providerMenuOpen, setProviderMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const providerMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    function onClickAway(e: MouseEvent) {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    }
    document.addEventListener("mousedown", onClickAway);
    return () => document.removeEventListener("mousedown", onClickAway);
  }, [menuOpen]);

  useEffect(() => {
    if (!providerMenuOpen) return;
    function onClickAway(e: MouseEvent) {
      if (!providerMenuRef.current?.contains(e.target as Node))
        setProviderMenuOpen(false);
    }
    document.addEventListener("mousedown", onClickAway);
    return () => document.removeEventListener("mousedown", onClickAway);
  }, [providerMenuOpen]);

  const user = data?.user;
  const initials = user ? initialsOf(user.name, user.email) : "??";
  const label = user?.email ?? "operator";

  async function onSignOut() {
    setMenuOpen(false);
    try {
      await signOut();
    } finally {
      // Drop the cached session and bounce to login regardless of network
      // outcome — the cookie has been requested-clear; on retry the user
      // will land at /login anyway.
      queryClient.setQueryData(["session"], null);
      navigate("/login", { replace: true });
    }
  }

  return (
    <header className="ad-top">
      <button
        type="button"
        className="ad-top-icon-btn ad-mobile-only ad-mobile-nav-toggle"
        aria-label="Open navigation"
        onClick={() => {
          if (document.body.hasAttribute("data-mobile-nav-open")) {
            document.body.removeAttribute("data-mobile-nav-open");
          } else {
            document.body.setAttribute("data-mobile-nav-open", "true");
          }
        }}
      >
        <Icons.Menu size={18} />
      </button>

      <div className="ad-top-search ad-desktop-only">
        <Icons.Search size={14} />
        <input placeholder="Search" />
        <kbd>⌘K</kbd>
      </div>

      {/* Provider switcher — visible when the operator belongs to more
          than one org. Single-membership operators don't need a chooser. */}
      {switcher.canSwitch && (
        <div ref={providerMenuRef} style={{ position: "relative" }}>
          <button
            type="button"
            className="ad-btn ad-btn-ghost ad-btn-sm"
            aria-haspopup="menu"
            aria-expanded={providerMenuOpen}
            onClick={() => setProviderMenuOpen((v) => !v)}
            disabled={switcher.isSwitching}
            style={{ display: "inline-flex", gap: 6, alignItems: "center" }}
          >
            <span style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {switcher.activeProvider?.name ?? "Select organization"}
            </span>
            <Icons.ChevronD size={12} />
          </button>
          {providerMenuOpen && (
            <div
              role="menu"
              className="ad-menu-pop"
              style={{
                position: "absolute",
                top: "calc(100% + 6px)",
                left: 0,
                minWidth: 220,
                padding: 6,
                color: "var(--ad-fg-1)",
                border: "1px solid var(--ad-border-1)",
                borderRadius: 14,
                boxShadow: "var(--ad-card-shadow)",
                zIndex: 100,
              }}
            >
              {switcher.providers.map((p) => {
                const isCurrent = p.id === switcher.activeProviderId;
                return (
                  <button
                    key={p.id}
                    type="button"
                    role="menuitem"
                    aria-current={isCurrent || undefined}
                    className={`ad-menu-item${isCurrent ? " is-current" : ""}`}
                    onClick={() => {
                      setProviderMenuOpen(false);
                      switcher.switchTo(p.id);
                    }}
                  >
                    <span className="ad-menu-item-body">
                      <span className="ad-menu-item-name">{p.name}</span>
                      <span className="ad-menu-item-sub" style={{ display: "block" }}>
                        {p.role}
                      </span>
                    </span>
                    {isCurrent && (
                      <Icons.Check size={14} className="ad-menu-item-check" />
                    )}
                  </button>
                );
              })}
              <div style={{ height: 1, background: "var(--ad-hairline)", margin: "4px 0" }} />
              <NavLink
                to="/organizations/new"
                role="menuitem"
                onClick={() => setProviderMenuOpen(false)}
                style={{
                  display: "block",
                  padding: "8px 10px",
                  color: "var(--ad-fg-2)",
                  borderRadius: 6,
                  fontSize: 13,
                  textDecoration: "none",
                }}
              >
                + Create organization
              </NavLink>
            </div>
          )}
        </div>
      )}

      <div className="ad-top-spacer" />

      <button
        className="ad-top-icon-btn"
        title={`Theme: ${THEME_PREF_LABEL[tweaks.themePref]}`}
        onClick={() => setTweak("themePref", nextThemePref(tweaks.themePref))}
        aria-label={`Theme: ${THEME_PREF_LABEL[tweaks.themePref]}. Change theme`}
      >
        {tweaks.themePref === "auto" ? <Icons.Window /> : tweaks.themePref === "dark" ? <Icons.Moon /> : <Icons.Sun />}
      </button>

      <button
        className="ad-top-icon-btn ad-desktop-only"
        title="Help"
        onClick={() =>
          window.open("https://docs.authensee.com/", "_blank")
        }
        aria-label="Help"
      >
        <Icons.Help />
      </button>

      {(data?.role === "owner" || data?.role === "admin") && (
        <span className="ad-desktop-only">
          <Btn
            kind="ghost"
            size="sm"
            leading={<Icons.Plus size={14} />}
            onClick={() => navigate("/team")}
          >
            Invite
          </Btn>
        </span>
      )}

      <div ref={menuRef} style={{ position: "relative" }}>
        <button
          type="button"
          className="ad-top-avatar"
          title={label}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((v) => !v)}
          style={{ cursor: "pointer", border: 0, padding: 0 }}
        >
          {initials}
        </button>
        {menuOpen && (
          <div
            role="menu"
            className="ad-menu-pop"
            style={{
              position: "absolute",
              top: "calc(100% + 6px)",
              right: 0,
              minWidth: 200,
              padding: 6,
              color: "var(--ad-fg-1)",
              border: "1px solid var(--ad-hairline)",
              borderRadius: 8,
              boxShadow: "var(--ad-card-shadow)",
              zIndex: 100,
            }}
          >
            <div
              style={{
                padding: "6px 10px",
                fontSize: 12,
                color: "var(--ad-fg-3)",
              }}
            >
              Signed in as
              <div
                style={{
                  fontSize: 13,
                  color: "var(--ad-fg-1)",
                  marginTop: 2,
                }}
              >
                {user?.email ?? "(unknown)"}
              </div>
            </div>
            <div
              style={{
                height: 1,
                background: "var(--ad-hairline)",
                margin: "4px 0",
              }}
            />
            <button
              type="button"
              role="menuitem"
              onClick={onSignOut}
              style={{
                width: "100%",
                textAlign: "left",
                padding: "8px 10px",
                background: "transparent",
                color: "var(--ad-fg-1)",
                border: 0,
                cursor: "pointer",
                borderRadius: 6,
                fontSize: 13,
              }}
            >
              Sign out
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
